import {
  legacy_createStore as createStore,
  combineReducers,
  applyMiddleware,
  AnyAction,
} from "redux";
import { thunk, ThunkDispatch } from "redux-thunk";
import { composeWithDevTools } from "@redux-devtools/extension";
import axios from "axios";
import {
  productCreateReducer,
  productDeleteReducer,
  productDetailsReducer,
  productListReducer,
  productUpdateReducer,
} from "./reducers/product.reducers";
import { imageUploadReducer } from "./reducers/upload.reducers";
import {
  analyticsReducer,
  orderBulkUpdateReducer,
  orderCreateReducer,
  orderDetailsReducer,
  orderListReducer,
  orderUpdateReducer,
} from "./reducers/order.reducers";
import { sidebarExpandReducer, SIDEBAR_EXPAND_DEFAULT } from "./reducers/sidebar.reducers";

const sidebarExpandFromStorage = localStorage.getItem("sidebarExpand")
  ? JSON.parse(localStorage.getItem("sidebarExpand")!)
  : SIDEBAR_EXPAND_DEFAULT;

const reducer = combineReducers({
  imageUpload: imageUploadReducer,
  productList: productListReducer,
  productDetails: productDetailsReducer,
  productCreate: productCreateReducer,
  productUpdate: productUpdateReducer,
  productDelete: productDeleteReducer,
  orderList: orderListReducer,
  orderCreate: orderCreateReducer,
  orderUpdate: orderUpdateReducer,
  orderBulkUpdate: orderBulkUpdateReducer,
  orderDetails: orderDetailsReducer,
  analytics: analyticsReducer,
  sidebarExpand: sidebarExpandReducer,
});

const preloadedState = {
  sidebarExpand: sidebarExpandFromStorage,
};

const middleware = [thunk];

const store = createStore(
  reducer,
  preloadedState,
  composeWithDevTools(applyMiddleware(...middleware))
);

// Get token from Zustand persisted state in localStorage
const getAuthToken = () => {
  try {
    const stored = localStorage.getItem("userInfo");
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.state?.userInfo?.token || null;
    }
  } catch {
    return null;
  }
  return null;
};

axios.interceptors.request.use((req) => {
  const token = getAuthToken();
  if (token) {
    req.headers["Authorization"] = `Bearer ${token}`;
  }

  if (!req.headers["Content-Type"]) req.headers["Content-Type"] = "application/json";

  return req;
});

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (token) p.resolve(token);
    else p.reject(error);
  });
  failedQueue = [];
};

const getRefreshToken = () => {
  try {
    const stored = localStorage.getItem("userInfo");
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.state?.userInfo?.refreshToken || null;
    }
  } catch {
    return null;
  }
  return null;
};

axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    if (err.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this was already the refresh request
      if (originalRequest.url?.includes("/refresh")) {
        if (localStorage.getItem("userInfo")) localStorage.removeItem("userInfo");
        window.location.href = "/account/login";
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshTokenValue = getRefreshToken();
      if (!refreshTokenValue) {
        if (localStorage.getItem("userInfo")) localStorage.removeItem("userInfo");
        window.location.href = "/account/login";
        return Promise.reject(err);
      }

      try {
        const { data } = await axios.post(`${import.meta.env.VITE_API_URL}/users/refresh`, {
          refreshToken: refreshTokenValue,
        });

        // Update tokens in localStorage (Zustand persist format)
        const stored = localStorage.getItem("userInfo");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.state?.userInfo) {
            parsed.state.userInfo.token = data.token;
            parsed.state.userInfo.refreshToken = data.refreshToken;
            localStorage.setItem("userInfo", JSON.stringify(parsed));
          }
        }

        originalRequest.headers["Authorization"] = `Bearer ${data.token}`;
        processQueue(null, data.token);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        if (localStorage.getItem("userInfo")) localStorage.removeItem("userInfo");
        window.location.href = "/account/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

export default store;
